import React, { forwardRef, useState, useEffect } from 'react';
import ReactSelect from 'react-select';
import type { StylesConfig, GroupBase } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { twMerge } from 'tailwind-merge';
import uniqid from 'uniqid';

import { isPrimitive } from 'helpers/forms';

interface SelectOption<TValue = string | number> {
  label: string;
  value: TValue;
}

interface FieldError {
  message?: string;
  [key: string]: unknown;
}

type SelectStyleOverrides = Record<string, unknown>;

const getBasicSelectStyles = (
  color: string,
  disabledColor: string,
  extraBasicStyles: SelectStyleOverrides,
): StylesConfig<SelectOption, boolean, GroupBase<SelectOption>> => ({
  control: (styles, { isDisabled, isFocused }) => ({
    ...styles,
    boxShadow: 'none',
    borderColor: color,
    // react-select's own `:focus` styling is on the hidden input, not the
    // visible control box — without this, Tab-ing to a select shows no
    // focus indicator at all (WCAG 2.4.7). `isFocused` covers keyboard and
    // mouse focus alike, matching this design system's navy-500 focus ring
    // used elsewhere (e.g. button.css).
    outline: isFocused ? '2px solid #506D95' : 'none',
    outlineOffset: isFocused ? '-1px' : undefined,
    padding: 0,
    minHeight: 40,
    backgroundColor: isDisabled ? disabledColor : 'transparent',
    borderRadius: 6,
    '&:hover': {
      borderColor: color,
    },
    ...extraBasicStyles,
  }),
  // react-select dims disabled singleValue/placeholder via `opacity: 0.5` on
  // its default styles, which can drop text below the WCAG 1.4.3 4.5:1
  // contrast ratio against `disabledBgColor`. Override with an explicit,
  // still-legible color and cancel the opacity dimming instead of relying on
  // it as the "disabled" affordance.
  singleValue: (styles, { isDisabled }) => ({
    ...styles,
    color: isDisabled ? '#42536B' : 'inherit',
    opacity: 1,
  }),
  placeholder: (styles, { isDisabled }) => ({
    ...styles,
    color: isDisabled ? '#42536B' : styles.color,
    opacity: 1,
  }),
  menu: (styles) => ({
    ...styles,
    border: '1px solid #C0CBD8',
    borderRadius: 6,
    boxShadow: 'none',
    marginTop: 2,
    marginBottom: 2,
    padding: 8,
  }),
  menuList: (styles) => ({
    ...styles,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    padding: 0,
  }),
  option: (styles, { isSelected, isFocused }) => ({
    ...styles,
    borderRadius: 6,
    padding: '8px 10px',
    backgroundColor: isSelected
      ? '#E1E7EF'
      : isFocused
        ? '#F5F8FA'
        : styles.backgroundColor,
    color: '#002D5D',
    fontWeight: isSelected ? 600 : styles.fontWeight,
  }),
  dropdownIndicator: (styles) => ({
    ...styles,
    color: '#506D95',
    ':hover': {
      color: '#002D5D',
    },
  }),
  clearIndicator: (styles) => ({
    ...styles,
    color: '#4B5563',
    ':hover': {
      color: '#4B5563',
    },
    svg: { width: 20, height: 20 },
  }),
});

const multiSelectOptionStyles: StylesConfig<
  SelectOption,
  boolean,
  GroupBase<SelectOption>
> = {
  multiValue: (styles, { isDisabled }) => ({
    ...styles,
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    height: 32,
    backgroundColor: 'white',
    border: '1px solid #C0CBD8',
    padding: isDisabled ? '0 7px 0 0' : 0,
    margin: '0px 4px 0px 0px',
    borderRadius: '6px',
    fontSize: 16,
    lineHeight: '24px',
  }),
  multiValueLabel: (styles) => ({
    ...styles,
    color: '#111827',
    fontSize: 16,
    lineHeight: '24px',
    padding: '0 8px 0 12px',
  }),
  multiValueRemove: (styles, { isDisabled }) => ({
    ...styles,
    color: '#002D5D',
    display: isDisabled ? 'none' : 'flex',
    alignItems: 'center',
    padding: '0 7px 0 0',
    ':hover': {
      cursor: 'pointer',
    },
    svg: { width: 14, height: 14 },
  }),
};

interface SelectProps<TOption extends SelectOption = SelectOption> {
  label?: string;
  labelFor?: string;
  fieldError?: FieldError;
  customClass?: string;
  selectClass?: string;
  selectWrapperClass?: string;
  isSearchable?: boolean;
  isOneRowLable?: boolean;
  helperText?: string;
  options?: TOption[];
  isMulti?: boolean;
  onChange?: (value: TOption | TOption[] | null) => void;
  value?: TOption['value'] | TOption | TOption[];
  errors?: unknown;
  clearInputErr?: () => void;
  disabled?: boolean;
  disabledBgColor?: string;
  labelInTop?: boolean;
  isCreateble?: boolean;
  extraStyles?: (
    borderColor: string,
    disabledBgColor: string,
  ) => SelectStyleOverrides;
  extraBasicStyles?: SelectStyleOverrides;
  required?: boolean;
  placeholder?: string;
  isClearable?: boolean;
  'data-testid'?: string;
  classNamePrefix?: string;
  'aria-label'?: string;
  name?: string;
  menuPlacement?: 'auto' | 'bottom' | 'top';
  // react-select pass-through for portalled menus
  menuPortalTarget?: HTMLElement | null;
  isLoading?: boolean;
  // RHF Controller `field` interop. Spread `{...field}` carries this.
  onBlur?: () => void;
}

type SelectValue = SelectOption | SelectOption[] | null | undefined;

const SelectImpl = forwardRef<unknown, SelectProps<SelectOption>>(
  (
    {
      label,
      labelFor = uniqid(),
      fieldError = {} as FieldError,
      customClass = '',
      selectClass = 'w-full',
      selectWrapperClass = '',
      isSearchable = true,
      isOneRowLable = true,
      helperText = '',
      options,
      isMulti = false,
      onChange,
      value: _value,
      errors: _errors,
      clearInputErr,
      disabled,
      disabledBgColor = '#F2F2F2',
      labelInTop,
      isCreateble,
      extraStyles = () => ({}),
      extraBasicStyles = {},
      required,
      placeholder = 'Select...',
      isClearable = false,
      'data-testid': dataTestId = 'select-field',
      isLoading,
      menuPortalTarget,
      'aria-label': ariaLabel,
      onBlur,
    },
    ref,
  ) => {
    const [borderColor, setBorderColor] = useState('#C0CBD8');
    const [isNotValid, setIsNotValid] = useState(false);

    const [inputValue, setInputValue] = useState('');
    const [value, setValue] = useState<SelectValue>(
      _value && !isPrimitive(_value) ? (_value as SelectValue) : [],
    );

    useEffect(() => {
      if (_value == null) {
        setValue(null);
      } else if (isPrimitive(_value)) {
        const selected = options?.find(
          (option) => option.value === _value || option.label === _value,
        );

        setValue(selected);
      } else {
        setValue(_value as SelectValue);
      }
    }, [_value, options]);

    const handleChange = (
      value: SelectOption | SelectOption[] | null,
    ): void => {
      if (isNotValid) clearInputErr?.();
      setValue(value || []);
      onChange?.(value);
    };

    const handleKeyDown = (event: React.KeyboardEvent): void => {
      if (!inputValue) return;

      switch (event.keyCode) {
        case 9: // Tab
        case 13: // Enter
        case 32: // Space
          createValueFromInput(event);
          break;
        default:
      }
    };

    const createValueFromInput = (event: React.SyntheticEvent): void => {
      if (inputValue && inputValue.length > 0) {
        const currentValue = Array.isArray(value) ? value : [];
        const newValue: SelectOption[] = [
          ...currentValue,
          {
            label: inputValue,
            value: inputValue,
          },
        ];

        setInputValue('');
        setValue(newValue);
        onChange?.(newValue);
        event.preventDefault();
      }
    };

    const labelStyles = twMerge(
      'font-semibold mt-3 mb-1 text-base leading-6 text-gray-700',
      !labelInTop && 'mb-5',
      !disabled && helperText && !isOneRowLable && 'h-16',
      !disabled && helperText && isOneRowLable && !labelInTop && 'h-11',
      isOneRowLable && 'flex h-6',
      customClass,
    );

    useEffect(() => {
      const isError = !!Object.keys(fieldError).length;

      setBorderColor(isError ? '#D91655' : '#C0CBD8');
      setIsNotValid(isError);
    }, [fieldError]);

    const CurrentSelect = isCreateble ? CreatableSelect : ReactSelect;

    const formatCreateLabel = (inputValue: string): string =>
      `Use "${inputValue}"`;
    const errorMessage = fieldError?.message;

    const selectRef = ref as never;

    const ariaLabelProps: {
      'aria-labelledby'?: string;
      'aria-label'?: string;
    } = {};

    if (label && labelFor) {
      ariaLabelProps['aria-labelledby'] = `${labelFor}-label`;
    } else if (ariaLabel) {
      ariaLabelProps['aria-label'] = ariaLabel;
    }

    return (
      <div className={twMerge('flex flex-col w-full', selectWrapperClass)}>
        <div
          className={twMerge(
            'flex',
            labelInTop ? 'flex-col items-start' : 'items-center',
          )}
        >
          {label && (
            <label
              id={`${labelFor}-label`}
              htmlFor={labelFor}
              className={labelStyles}
            >
              {label}
              {required && !disabled ? (
                <span
                  aria-hidden="true"
                  className="ml-1 text-critical-600"
                  data-testid={`${dataTestId}-required-indicator`}
                >
                  *
                </span>
              ) : null}
            </label>
          )}
          <div
            className={`flex flex-col ${selectClass}`}
            data-testid={dataTestId}
          >
            {isMulti && !options ? (
              <CreatableSelect<SelectOption, true>
                id={labelFor}
                styles={
                  {
                    ...getBasicSelectStyles(
                      borderColor,
                      disabledBgColor,
                      extraBasicStyles,
                    ),
                    ...multiSelectOptionStyles,
                  } as StylesConfig<SelectOption, true>
                }
                ref={selectRef}
                components={{
                  DropdownIndicator: () => null,
                  IndicatorSeparator: () => null,
                }}
                placeholder="Type..."
                inputValue={inputValue}
                isClearable={isClearable}
                isMulti
                menuIsOpen={false}
                onChange={(newValue) =>
                  handleChange(newValue as SelectOption[])
                }
                onBlur={(event) => createValueFromInput(event)}
                onInputChange={(newInputValue) => {
                  if (isNotValid) clearInputErr?.();
                  setInputValue(newInputValue);
                }}
                onKeyDown={handleKeyDown}
                value={value as SelectOption[]}
                {...ariaLabelProps}
                isDisabled={disabled}
              />
            ) : isMulti && options ? (
              <CurrentSelect<SelectOption, true>
                isMulti
                id={labelFor}
                styles={
                  {
                    ...getBasicSelectStyles(
                      borderColor,
                      disabledBgColor,
                      extraBasicStyles,
                    ),
                    ...multiSelectOptionStyles,
                    ...extraStyles(borderColor, disabledBgColor),
                  } as StylesConfig<SelectOption, true>
                }
                ref={selectRef}
                isSearchable={isSearchable}
                options={options}
                value={value as SelectOption[]}
                onChange={(newValue) =>
                  handleChange(newValue as SelectOption[])
                }
                components={{
                  IndicatorSeparator: () => null,
                }}
                {...ariaLabelProps}
                menuPortalTarget={menuPortalTarget}
                placeholder={disabled ? 'Not selected' : placeholder}
                isDisabled={disabled}
                formatCreateLabel={formatCreateLabel}
                isClearable={isClearable}
                onBlur={onBlur}
              />
            ) : (
              <CurrentSelect<SelectOption, false>
                id={labelFor}
                styles={
                  {
                    ...getBasicSelectStyles(
                      borderColor,
                      disabledBgColor,
                      extraBasicStyles,
                    ),
                    ...extraStyles(borderColor, disabledBgColor),
                  } as StylesConfig<SelectOption, false>
                }
                ref={selectRef}
                isSearchable={isSearchable}
                options={options}
                value={value as SelectOption}
                onChange={(newValue) =>
                  handleChange(newValue as SelectOption | null)
                }
                components={{
                  IndicatorSeparator: () => null,
                }}
                {...ariaLabelProps}
                menuPortalTarget={menuPortalTarget}
                placeholder={disabled ? 'Not selected' : placeholder}
                isDisabled={disabled}
                isLoading={isLoading}
                formatCreateLabel={formatCreateLabel}
                isClearable={isClearable}
                onBlur={onBlur}
              />
            )}
            {errorMessage && !disabled && (
              <span
                id={`${labelFor}-error`}
                role="status"
                className="font-sm pt-1 text-critical-600 bg-white"
              >
                {errorMessage}
              </span>
            )}
            {helperText && !disabled && !errorMessage && (
              <span id={`${labelFor}-helper`} className="font-sm pt-1 bg-white">
                {helperText}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  },
);

SelectImpl.displayName = 'Select';

// Generic re-export so callers can write `<Select<MyOption> ... />` without `as never`.
// The forwardRef type erases generics; this cast restores them at the type layer
// without changing the runtime component.
export const Select = SelectImpl as unknown as (<
  TOption extends SelectOption = SelectOption,
>(
  props: SelectProps<TOption> & { ref?: React.Ref<unknown> },
) => React.ReactElement) & {
  displayName?: string;
};

export type { SelectOption, SelectProps };

export default Select;
